import {
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  Tab,
  Tabs,
  Typography,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Paper,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { Add, ContentCopy } from '@mui/icons-material';
import React, { useEffect, useMemo, useState } from 'react';
import EntityActions from './EntityActions';
import * as UIAction from '../../redux/actions/ui.actions';
import EntityParams from './EntityParams';
import FireLoading from '../../shared/FireLoading';
import { goBack, setDialogSize, setNextStep, openGoogleWidget } from '../../redux/actions';
import FileAcceptor from '../uploadFiles/FileAcceptor';
import SFTPFileList from '../SFTP/SFTPFileList';
import GoogleSheetList from '../googleSheets/SheetList';
import JobsHistoryList from '../jobsHistory/JobsHistoryList';
import { useSelector } from 'react-redux';
import LoadingDial from '../loading/LoadingDial';
import Mapping from '../mapping';
import { preLink } from '../availableSources/SourceCardUtil';
import { preLink as preLinkConnector } from '../../components/availableConnectorsV2/ConnectorCardUtil';
import sortByTenantIdFn from '../../utils/sortByTenantId';
import { Webhook } from '../trigger';


const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      sx={{ p: 0, ...props?.sx, display: value === index ? 'flex' : 'none', flex: 1 }}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

const areOptionsValid = (options) => options && Array.isArray(options) && options.length > 0;

const shouldRenderFilters = (entity = undefined) => {
  if (!entity) return false;

  const { filters, config } = entity;

  if (!config || !filters || filters.length === 0) return false;

  return filters.some(({ key }) => areOptionsValid(config[key]));
};

const buildParamsFromFilters = ({ filters, config }) => {
  return filters.reduce((params, filter) => {
    const { key, targetKey, type, label, description } = filter;
    const options = config[key];

    if (areOptionsValid(options)) {
      params[targetKey] = {
        label,
        description,
        type,
        options,
      };
    }

    return params;
  }, {});
};

const EntityConfig = (props) => {
  const {
    entity,
    linkedSource,
    linkedTarget,
    linkedConnector,
    flowId,
    isConnected,
    connectParams,
    tenantId,
    listener,
    hideBackButtons,
    linkList: linkListProp,
    multipleTenantLinks,
    fetchLinkedSource,
    fetchLinkedConnector,
    nextStep,
    localization,
  } = props;
  const [isLoading, setLoading] = useState(false);
  const isFile = useMemo(() => entity.entityType === 'source' && entity.tap === 'file', [entity]);
  const isSftpFile = useMemo(() => entity.entityType === 'source' && entity.tap === 'sftp-files', [entity]);
  const isGoogleSheet = useMemo(() => (entity.entityType === 'source' && entity.tap === 'google-sheets-beta') || (entity.entityType === 'connector' && entity.id === 'google-sheets'), [entity]);
  const isGoogleDrive = useMemo(() => (entity.entityType === 'source' && entity.tap === 'google-drive') || (entity.entityType === 'connector' && entity.id === 'google-drive'), [entity]);
  const isSharepoint = useMemo(() => (entity.entityType === 'source' && entity.tap === 'sharepoint') || (entity.entityType === 'connector' && entity.id === 'sharepoint'), [entity]);
  const hasGooglePicker = useMemo(() => entity.google_tokens !== undefined, [entity]);
  const showFileSelector = (isGoogleDrive || isSharepoint || isGoogleSheet) && !entity.disable_file_selector && !hasGooglePicker;
  const [value, setValue] = useState(3);
  const [connectionName, setConnectionName] = useState('');
  const jobHistoryEnabled = useSelector((state) => state.preferences.jobHistoryEnabled);
  const disableSubtenantCreation = useSelector((state) => state.preferences.disableSubtenantCreation);
  const subtenantDelimiter = useSelector((state) => state.preferences.featureFlags?.subtenantDelimiter ?? '_');
  const flow = useSelector((state) => state.flows.supportedFlows[flowId]);
  const subTenantsEnabled = useSelector(state => state.configuration.subTenantsEnabled)
  const showSubTenantsId = useSelector(state => state.configuration.showSubTenantsId)
  const schemas = useSelector((state) => state.schemas);
  const [account, setAccount] = useState(0);
  const [linkListPropLength, setLinkListPropLength] = React.useState(linkListProp.length);
  const [linkList, setLinkList] = React.useState(linkListProp);
  const hasSchemaToMap = useMemo(
    () => schemas && Object.keys(schemas)?.length > 0 && schemas[flowId],
    [schemas, flowId, schemas, nextStep]
  );

  React.useEffect(() => {
    setLinkListPropLength(linkListProp.length);
  }, [linkListProp.length]);

  React.useEffect(() => {
    setLinkList(linkListProp);
  }, [linkListPropLength]);

  React.useEffect(() => {
    if (entity?.tenant_id?.includes(subtenantDelimiter)) {
      const index = linkList.findIndex((x) => x.tenant_id === entity?.tenant_id);
      if (index >= 0) {
        const link = linkList[index];
        handleAccountChange({}, link, index);
      }
    }
  }, []);

  const fetchCurrentEntity = async (tenant = undefined) => {
      const user = tenant ? tenant : tenantId
      setLoading(true);
      if (entity.entityType === 'source') await fetchLinkedSource(entity.tap, user);
      else await fetchLinkedConnector(entity.id, user);
      setLoading(false);
  }

  React.useEffect(() => {
    if (linkListProp.length === 0) fetchCurrentEntity();
  }, []);

  // Function to search the next possible tenant id
  // It first tries to search for the root tenant
  // And then it adds a undescore and a incremental integer
  const getNextTenantId = () => {
    if (!multipleTenantLinks) return tenantId;
    let tenantIdToSearch = tenantId;
    let nextIndex = tenantId.split(subtenantDelimiter).length > 1 ? tenantId.split(subtenantDelimiter)[1] + 1 : 1;
    do {
      const foundLink = linkList.find((link) => link.tenant_id === tenantIdToSearch);
      if (foundLink === undefined) return tenantIdToSearch;
      tenantIdToSearch = `${tenantId}${subtenantDelimiter}${nextIndex}`;
      nextIndex++;
    } while (true);
  };

  const addLoadingLink = (newTenantId) => {
    setLinkList((oldLinkList) => {
      const newLinkList = [...oldLinkList, { loading: true, tenant_id: newTenantId }].sort(
        sortByTenantIdFn(newTenantId, subtenantDelimiter)
      );
      return newLinkList;
    });
  };

  const handleAccountChange = (event, link, index) => {
    setAccount(index);
    UIAction.selectEntity(
      {
        ...link,
        label: `${link.label}`,
      },
      entity.entityType
    );
    UIAction.clearFieldMapSelection(entity.entityType === 'connector');
    fetchCurrentEntity(link.tenant_id)
  };

  useEffect(() => {
    if (isFile) {
      setValue(0);
    }

    if (isSftpFile) {
      setValue(1);
    }

    if (showFileSelector) {
      setValue(2);
    }
  }, []);

  useEffect(() => {
    if (value === 0 || value === 1 || value === 2) {
      setDialogSize('829px', '676px');
    }
  }, [value]);

  useEffect(() => {
    if(nextStep === 'credentials'){
      setValue(4)
      setNextStep("")
    }

    if(nextStep === 'triggers'){
      setValue(7)
      setNextStep("")
    }

    if(nextStep === 'selectFile'){
      openGoogleWidget(entity, tenantId, flowId)
      setNextStep("")
    }
  },[nextStep])


  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // checks if the nextStep is mapping in order to sent the user to the custom mapping page
  // and also check if the exists a schema for the selected flow id.
  useEffect(() => {
    if (nextStep === 'mapping' && hasSchemaToMap) setValue(6);
  }, [hasSchemaToMap]);

  const handleSaveChanges = (e) => {
    e.preventDefault();
    if (value === 0) {
      setValue(1);
      setTimeout(() => e.target.click(), 500);
    }
  };

  const hasFilters = shouldRenderFilters(entity);

  return (
    <Box display="flex" flexDirection="column" justifyContent="space-between" flex="1" width="100%" pt={1.5}>
      {isLoading ? (
        <Box display="flex" flexDirection="column" flex={1} justifyContent="center" alignItems="center" gap={4}>
          <LoadingDial />
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" flex={1}>
          <Box
            display="flex"
            width="100%"
            justifyContent="space-between"
            flex="0"
            gap={2}
            mt={1}
            mb={2}
            sx={{ position: 'sticky', top: 0, background: 'white', zIndex: 999 }}
          >
            {multipleTenantLinks &&
              subTenantsEnabled &&
              (entity.entityType === 'source' || entity.entityType === 'connector') && (
                <Box display="flex" alignItems="center" sx={{ maxWidth: 240 }}>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <InputLabel id="accountLabel">Account</InputLabel>
                    <Select
                      label="Account"
                      labelId="accountLabel"
                      value={account}
                      sx={{
                        '.hotglue-w-MuiSelect-select p:last-child:not(:first-of-type)': {
                          display: 'none',
                        },
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        PaperProps: {
                          sx: {
                            width: '342px',
                            '& ul': {
                              p: 2,
                            },
                          },
                        },
                      }}
                    >
                      <Box component="li" mb={2}>
                        <Typography fontSize="14px" fontWeight={700}>
                          Switch Connections
                        </Typography>
                      </Box>
                      {linkList.map((link, index) => {
                        const hasConnectionName = !!link.connection_name;
                        return (
                          <MenuItem
                            key={`${link.tenant_id}-${index}`}
                            name={link}
                            value={index}
                            onClick={(e) => handleAccountChange(e, link, index)}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.5,
                              alignItems: 'start',
                            }}
                          >
                            <Typography
                              sx={{
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {showSubTenantsId
                                ? link.connection_name || `Connection #${link?.tenant_id?.split('/')?.[1] || 1}`
                                : link.connection_name || link.tenant_id}
                            </Typography>
                            {showSubTenantsId && (
                              <Typography
                                variant="body2"
                                fontWeight={300}
                                color="text.lightish"
                                sx={{
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {link.tenant_id}
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      })}
                      {!disableSubtenantCreation && (
                        <Box
                          component={'li'}
                          sx={{ mt: 2, borderTop: ({ palette }) => `1px solid ${palette.divider}` }}
                        >
                          <Box gap={1} display="flex" flexDirection="column" mt={1}>
                            <Typography fontSize="14px" fontWeight={700}>
                              Add New
                            </Typography>
                            <TextField
                              size="small"
                              variant="standard"
                              label={'Enter Connection Name'}
                              onKeyDown={(e) => e.stopPropagation()}
                              value={connectionName}
                              onChange={(e) =>
                                !e.target.value.includes(subtenantDelimiter) && setConnectionName(e.target.value)
                              }
                            />
                            <Button
                              variant="contained"
                              color="accent"
                              onClick={async () => {
                                const newTenantId = getNextTenantId();
                                entity.entityType === 'source'
                                  ? await preLink({
                                      flow,
                                      source: { ...entity, connection_name: connectionName.trim() },
                                      shouldSkipCopy: true,
                                      tenantId: newTenantId,
                                      setLoading,
                                    })
                                  : await preLinkConnector({
                                      flow,
                                      connector: { ...entity, connection_name: connectionName.trim() },
                                      shouldSkipCopy: true,
                                      tenantId: newTenantId,
                                      setLoading,
                                    });

                                addLoadingLink(newTenantId);
                              }}
                            >
                              Connect Account
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Select>
                  </FormControl>
                </Box>
              )}
            <Tabs
              value={value}
              onChange={handleChange}
              sx={{
                minHeight: '32px',
                '& button': {
                  minHeight: '32px',
                  textTransform: 'initial',
                  fontSize: '14px',
                  p: '12px',
                },
              }}
            >
              <Tab
                label={localization?.connectorManagementTabs?.uploadFiles || 'Upload Files'}
                value={0}
                aria-controls="tab-upload-files"
                sx={{ display: isFile ? 'initial' : 'none' }}
              />
              <Tab
                label="Select SFTP Files"
                value={1}
                aria-controls="tab-select-sftp-files"
                sx={{ display: isSftpFile ? 'initial' : 'none' }}
              />
              <Tab
                label={isGoogleSheet ? 'Select Sheets' : 'Select Files'}
                value={2}
                aria-controls="tab-select-sheets"
                sx={{ display: showFileSelector ? 'initial' : 'none' }}
              />
              <Tab
                label={localization?.connectorManagementTabs?.quickActions || 'Quick Actions'}
                value={3}
                aria-controls="tab-quick-actions"
              />
              <Tab
                label={localization?.connectorManagementTabs?.credentials || 'Credentials'}
                aria-controls="tab-credentials"
                value={4}
                sx={{ display: (entity?.connect_ui_params || hasFilters) && !isFile ? 'initial' : 'none' }}
              />
              <Tab
                label={localization?.connectorManagementTabs?.jobHistory || 'Job History'}
                value={5}
                aria-controls="tab-job-history"
                sx={{ display: jobHistoryEnabled ? 'initial' : 'none' }}
              />
              <Tab
                label={localization?.connectorManagementTabs?.mapping || 'Mapping'}
                value={6}
                aria-controls="tab-mapping"
                sx={{ display: hasSchemaToMap ? 'initial' : 'none' }}
              />
              <Tab
                label={localization?.connectorManagementTabs?.triggers || 'Triggers'}
                value={7}
                aria-controls="tab-triggers"
                sx={{ display: entity?.show_trigger_url ? 'initial' : 'none' }}
              />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            <FileAcceptor />
          </TabPanel>
          <TabPanel value={value} index={1} sx={{ display: value === 1 ? 'flex' : 'none', flex: 1, p: 0 }}>
            <SFTPFileList />
          </TabPanel>
          {/* TODO: handle google drive feature flag not render this tab */}
          <TabPanel value={value} index={2} sx={{ display: value === 2 ? 'flex' : 'none', flex: 1, p: 0 }}>
            <GoogleSheetList />
          </TabPanel>
          <TabPanel value={value} index={3} sx={{ p: 0 }}>
            <EntityActions entity={entity} setLoading={setLoading} />
          </TabPanel>
          <TabPanel
            index={4}
            value={value}
            sx={{
              overflow: 'hidden',
              overflowY: 'auto',
              p: 0,
            }}
          >
            <EntityParams
              entity={
                hasFilters
                  ? {
                      ...entity,
                      type: 'api',
                      connect_ui_params: buildParamsFromFilters(entity),
                    }
                  : entity
              }
              entityType={entity.entityType}
              flowId={flowId}
              widgetTenant={tenantId}
              connectParams={connectParams}
              isConnected={isConnected}
              multipleTenantLinks={multipleTenantLinks}
              linkedSource={linkedSource}
              linkedTarget={linkedTarget}
              linkedConnector={linkedConnector}
              setLoading={setLoading}
              listener={listener}
              account={account}
              px={'0px'}
              formStyles={{ pt: 2 }}
            />
          </TabPanel>
          <TabPanel index={5} value={value} sx={{ p: 0 }}>
            <JobsHistoryList entity={entity} />
          </TabPanel>
          <TabPanel value={value} index={6} sx={{ flex: 1, p: 0 }}>
            <Mapping />
          </TabPanel>
          <TabPanel value={value} index={7} sx={{ py: 1 }}>
            <Webhook tenantId={tenantId} flowId={flowId} entity={entity} />
          </TabPanel>
        </Box>
      )}
      {value !== 0 && value !== 1 && value !== 2 && value !== 6 && (
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          pt={2}
          pb={3}
          position="sticky"
          bottom={0}
          bgcolor="background.paper"
        >
          {!hideBackButtons && (
            <Button variant="outlined" color="text" disabled={!!isLoading} onClick={goBack} sx={{ px: 4 }}>
              Back
            </Button>
          )}
          {(entity?.connect_ui_params || hasFilters) && value === 4 && (
            <Button
              disabled={!!isLoading}
              variant="contained"
              onClick={(e) => value === 0 && handleSaveChanges(e)}
              color="accent"
              sx={{ fontSize: '14px', px: 4 }}
              form="app-params-form"
              type="submit"
            >
              Save Changes
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default EntityConfig;
